import Component from '@glimmer/component'
import { on } from '@ember/modifier'
import { fn, hash } from '@ember/helper'
import {
  isSameDay,
  format,
  formatISO,
  startOfMonth,
  startOfWeek,
  endOfMonth,
  endOfWeek,
  eachDayOfInterval,
  isBefore,
  isAfter,
  isSameMonth,
  isToday,
} from 'date-fns'
import { chunk } from 'lodash'
import { eq, or } from 'ember-truth-helpers'

type DayState = 'unselected' | 'selected' | 'selected_edge' | 'other_month' | 'disabled'

function dayState(
  day: Date,
  center: Date,
  { start, end }: { start?: Date; end?: Date },
  { min, max }: { min?: Date; max?: Date },
): DayState {
  if (!isSameMonth(day, center)) return 'other_month'
  if ((min && !isSameDay(day, min) && isBefore(day, min)) || (max && !isSameDay(day, max) && isAfter(day, max)))
    return 'disabled'
  if ((start && isSameDay(day, start)) || (end && isSameDay(day, end))) return 'selected_edge'
  if (start && isBefore(start, day) && end && isBefore(day, end)) return 'selected'
  return 'unselected'
}

function classesForDay(dayState: DayState) {
  switch (dayState) {
    case 'other_month':
      return 'pointer-events-none invisible'
    case 'selected_edge':
      return 'bg-neutral-300 hover:bg-neutral-75 cursor-pointer'
    case 'selected':
      return 'bg-neutral-50 hover:bg-neutral-75 cursor-pointer'
    case 'disabled':
      return 'bg-neutral-50 pointer-events-none cursor-default'
    case 'unselected':
      return 'hover:bg-neutral-75 cursor-pointer'
    default:
      return ''
  }
}

// Add a thick border (but transparent for other cells to keep things aligned). Replace with outline for selected_edge
function classesForToday(dayState: DayState, isToday: boolean) {
  if (isToday) {
    switch (dayState) {
      case 'selected_edge':
        return 'border-2 border-transparent font-semibold text-white hover:text-neutral-400 outline outline-2 outline-offset-2 outline-neutral-400'
      case 'disabled':
        return 'border-2 border-neutral-200 font-semibold text-neutral-250'
      default:
        return 'border-2 border-neutral-400 font-semibold text-neutral-400'
    }
  }
  switch (dayState) {
    case 'selected_edge':
      return 'border-2 border-transparent text-white hover:text-neutral-300'
    case 'disabled':
      return 'border-2 border-transparent text-neutral-250'
    default:
      return 'border-2 border-transparent text-neutral-300'
  }
}

// Depending which edge (start or end of range), round one side
function classesForSelectedEdge(day: Date, start?: Date, end?: Date) {
  const classes = []
  if (start && isSameDay(day, start)) classes.push('rounded-l-lg')
  if (end && isSameDay(day, end)) classes.push('rounded-r-lg')
  // allows for use as a single date picker - if only start date given, round both sides.
  if (start && !end && isSameDay(day, start)) classes.push('rounded-r-lg')
  return classes.join(' ')
}

interface Signature {
  Element: HTMLTableElement
  Args: {
    center: Date
    onSelectDate: (date: Date) => unknown
    /** days equal & after this date will be selected/highlighted */
    start?: Date
    /** days equal & before this date will be selected/highlighted */
    end?: Date
    /** days before this date will be disabled */
    min?: Date
    /** days after this date will be disabled */
    max?: Date
  }
}

export class CalendarTable extends Component<Signature> {
  get weekRows(): Date[][] {
    const weekStartsOn = 1 // monday
    const start = startOfWeek(startOfMonth(this.args.center), { weekStartsOn })
    const end = endOfWeek(endOfMonth(this.args.center), { weekStartsOn })
    const range = eachDayOfInterval({ start, end })
    return chunk(range, 7)
  }

  get weekDays() {
    return this.weekRows[0].map((date) => format(date, 'EEEEEE'))
  }

  /*
   * border-collapse: separate so that all cells to have a border
   * border-spacing removes horizontal gaps and adds small gap between rows (enough for outline to not overlap other cells)
   */
  <template>
    <table class="table-fixed border-separate border-spacing-x-0 border-spacing-y-1" ...attributes>
      <thead>
        <tr>
          {{#each this.weekDays as |weekDay|}}
            <th class="h-10 w-[calc(100%/7)] text-center font-normal text-neutral-250">{{weekDay}}</th>
          {{/each}}
        </tr>
      </thead>
      <tbody>
        {{#each this.weekRows as |week|}}
          <tr>
            {{#each week as |day|}}
              {{#let (dayState day @center (hash start=@start end=@end) (hash min=@min max=@max)) as |dayState|}}
                <td
                  class="h-10 text-center
                    {{classesForDay dayState}}
                    {{classesForToday dayState (isToday day)}}
                    {{classesForSelectedEdge day @start @end}}"
                  role="button"
                  data-test-day={{formatISO day (hash representation="date")}}
                  data-test-day-selected={{or (eq dayState "selected") (eq dayState "selected_edge")}}
                  data-test-day-state={{dayState}}
                  {{on "click" (fn @onSelectDate day)}}
                >
                  {{format day "d"}}
                </td>
              {{/let}}
            {{/each}}
          </tr>
        {{/each}}
      </tbody>
    </table>
  </template>
}
